import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Effect from 'effect/Effect';
import { pipe } from 'effect/Function';
import { CategoryService } from '@/effects/categories/category.service';
import { MainLayer } from '@/effects/layers';
import { runEffect } from '@/effects/runtime';
import { QUERY_KEYS } from '@/lib/constants';
import type {
  CreateCategoryParams,
  UpdateCategoryParams,
  UpdateCategorySplitsParams,
} from '@/effects/categories/category.service';

export function useCreateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['category', 'create'],
    mutationFn: async (params: CreateCategoryParams) => {
      const program = pipe(
        CategoryService,
        Effect.flatMap((service) => service.create(params)),
        Effect.provide(MainLayer)
      );

      return runEffect(program);
    },
    onSuccess: (_, params) => {
      // Invalidate group categories
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GROUP_CATEGORIES, params.groupId],
      });
    },
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['category', 'update'],
    mutationFn: async (params: UpdateCategoryParams) => {
      const program = pipe(
        CategoryService,
        Effect.flatMap((service) => service.update(params)),
        Effect.provide(MainLayer)
      );

      return runEffect(program);
    },
    onSuccess: (data, params) => {
      // Update the category in the cache
      queryClient.setQueryData(
        [QUERY_KEYS.GROUP_CATEGORIES, data.group_id],
        (old: unknown) => {
          if (!Array.isArray(old)) return old;

          return old.map((category) =>
            (category as { id: string }).id === params.categoryId ? data : category
          );
        }
      );

      // Invalidate category summary and splits
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CATEGORY_SUMMARY, params.categoryId],
      });
    },
  });
}

export function useUpdateCategorySplits() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['category', 'updateSplits'],
    mutationFn: async (params: UpdateCategorySplitsParams) => {
      const program = pipe(
        CategoryService,
        Effect.flatMap((service) => service.updateSplits(params)),
        Effect.provide(MainLayer)
      );

      return runEffect(program);
    },
    onSuccess: (data, params) => {
      // Update the splits in the cache
      queryClient.setQueryData(
        [QUERY_KEYS.CATEGORY_SPLITS, params.categoryId],
        data
      );

      // Update splits in the group categories cache
      queryClient.setQueriesData(
        { queryKey: [QUERY_KEYS.GROUP_CATEGORIES] },
        (old: unknown) => {
          if (!Array.isArray(old)) return old;

          return old.map((category) =>
            (category as { id: string }).id === params.categoryId
              ? { ...category, splits: data }
              : category
          );
        }
      );

      // Invalidate category summary as splits affect calculations
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CATEGORY_SUMMARY, params.categoryId],
      });
    },
  });
}